import _ from 'lodash';
import {
  channelLabel,
  copyLightProfilePoints,
  LIGHT_LIMITS,
  lightCatalogErrors,
  profileError
} from './light_profiles';

// Build a complete replacement catalog without touching Time, IO or zone settings.
// The caller owns confirmation and the ordinary revision-aware save transaction.
export function buildLightCopy(catalog, request, cycles = {}, baseline = catalog) {
  const fail = (message) => ({ errors: [message] });
  const { kind, source, target } = request ?? {};
  if (!['zone', 'subzone', 'profile'].includes(kind) || !source || !target)
    return fail('Choose a copy scope, source and destination.');
  const validIdentity = (identity) =>
    Number.isInteger(identity.zone) &&
    identity.zone >= 0 &&
    identity.zone <= 8 &&
    (kind === 'zone' ||
      (Number.isInteger(identity.subzone) && identity.subzone >= 0 && identity.subzone < 4)) &&
    (kind !== 'profile' ||
      (Number.isInteger(identity.channel) && identity.channel >= 0 && identity.channel < 6));
  if (!validIdentity(source) || !validIdentity(target))
    return fail('Invalid copy source or destination.');
  const matches = (item, identity) =>
    item.zone === identity.zone &&
    (kind === 'zone' || item.subzone === identity.subzone) &&
    (kind !== 'profile' || item.channel === identity.channel);
  if (matches(source, target)) return fail('Choose a different copy destination.');
  if (
    !Array.isArray(catalog?.channels) ||
    !Array.isArray(catalog?.subzones) ||
    !Array.isArray(catalog?.zones)
  )
    return fail('The complete lighting catalog is required.');
  const scopeExists = (identity) =>
    kind === 'profile'
      ? catalog.channels.some((channel) => matches(channel, identity))
      : kind === 'zone'
        ? [0, 1, 2, 3].every(
            (subzone) =>
              catalog.subzones.filter(
                (item) => item.zone === identity.zone && item.subzone === subzone
              ).length === 1
          )
        : catalog.subzones.filter((subzone) => matches(subzone, identity)).length === 1;
  if (!scopeExists(source) || !scopeExists(target))
    return fail('The copy source or destination is no longer available.');
  const cycle = cycles[target.zone];
  if (
    !cycle ||
    !Number.isInteger(cycle.start) ||
    !Number.isInteger(cycle.end) ||
    cycle.start < 0 ||
    cycle.start >= 1440 ||
    cycle.end <= cycle.start ||
    cycle.end - cycle.start > 1440
  )
    return fail('The destination zone Time settings are required.');

  const next = _.cloneDeep(catalog);
  const isShared = (channel) =>
    channel.subzone === 0 &&
    channel.channel === 0 &&
    catalog.zones.some((zone) => zone.zone === channel.zone && zone.shared_toplight === 1);
  const sources = catalog.channels.filter(
    (channel) => matches(channel, source) && (kind !== 'subzone' || !isShared(channel))
  );
  const retainedShared =
    kind === 'subzone'
      ? catalog.channels.find((channel) => matches(channel, target) && isShared(channel))
      : null;
  if (retainedShared && sources.some((channel) => channel.channel === 0))
    return fail(
      'Channel 1 at the destination is the shared top light. Choose another subzone or copy individual profiles.'
    );
  let copiedChannels;
  if (kind === 'profile') {
    const destination = next.channels.find((channel) => matches(channel, target));
    destination.points = copyLightProfilePoints(sources[0].points ?? []);
    copiedChannels = [destination];
  } else {
    copiedChannels = sources.map((channel) => ({
      zone: target.zone,
      subzone: kind === 'zone' ? channel.subzone : target.subzone,
      channel: channel.channel,
      name: channel.name,
      minimum: channel.minimum,
      maximum: channel.maximum,
      points: copyLightProfilePoints(channel.points ?? [])
    }));
    next.channels = [
      ...next.channels.filter(
        (channel) => !matches(channel, target) || (kind === 'subzone' && isShared(channel))
      ),
      ...copiedChannels
    ];
    next.subzones = next.subzones.map((subzone) => {
      if (!matches(subzone, target)) return subzone;
      const from = catalog.subzones.find(
        (item) =>
          item.zone === source.zone &&
          item.subzone === (kind === 'zone' ? subzone.subzone : source.subzone)
      );
      const allocated = next.channels
        .filter((channel) => channel.zone === subzone.zone && channel.subzone === subzone.subzone)
        .reduce((mask, channel) => mask | (1 << channel.channel), 0);
      const preserveMask = retainedShared ? 1 : 0;
      const main = retainedShared && subzone.main_channel === 0 ? 0 : from.main_channel;
      return {
        ...subzone,
        master: from.master,
        enabled: from.enabled,
        enabled_mask:
          ((from.enabled_mask & ~preserveMask) | (subzone.enabled_mask & preserveMask)) & allocated,
        visible_mask:
          ((from.visible_mask & ~preserveMask) | (subzone.visible_mask & preserveMask)) & allocated,
        main_channel: allocated & (1 << main) && main !== 255 ? main : 255
      };
    });
    if (kind === 'zone') {
      const from = catalog.zones.find((zone) => zone.zone === source.zone);
      next.zones = next.zones.map((zone) =>
        zone.zone === target.zone ? { ...from, zone: target.zone } : zone
      );
      next.advanced_zone_mask =
        (next.advanced_zone_mask & ~(1 << target.zone)) |
        (catalog.advanced_zone_mask & (1 << source.zone) ? 1 << target.zone : 0);
    }
  }

  // Even identical retained-invalid curves must pass destination Time validation
  // when explicitly copied. Other untouched profiles retain normal save semantics.
  const errors = copiedChannels.flatMap((channel) => {
    const error = profileError(channel.points, cycle);
    return error ? [`${channelLabel(channel)}: ${error}`] : [];
  });
  errors.push(...lightCatalogErrors(next, cycles, baseline));
  for (let zone = 0; zone < 9; zone++) {
    if (next.channels.filter((channel) => channel.zone === zone).length > LIGHT_LIMITS.zoneChannels)
      errors.push(`Zone ${zone + 1} exceeds its 24-channel capacity.`);
  }
  if (errors.length) return { errors: [...new Set(errors)] };
  next.channels.sort((a, b) => a.zone - b.zone || a.subzone - b.subzone || a.channel - b.channel);
  return { catalog: next, errors: [] };
}
